import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from '../../components/shared/Alert';
import axiosInstance from '../../utils/axiosConfig';
import { getApiErrorMessage } from '../../utils/apiError';
import { UUID_REGEX } from '../../utils/constants';
import { ConsentPageContent } from './ConsentPage';
import { PatientScreenersContent } from './PatientScreeners';

const TERMINAL_STATUSES = new Set(['COMPLETED', 'SYNCED', 'SYNC_FAILED', 'CANCELLED']);
const SUCCESS_TERMINAL_STATUSES = new Set(['COMPLETED', 'SYNCED']);

type CompletionOutcome = 'success' | 'cancelled' | 'sync_failed';

function resolveCompletionOutcome(
  consentWorkflowId: string | null,
  consentStatus: string | null,
  screenerWorkflowId: string | null,
  screenerStatus: string | null,
): CompletionOutcome {
  const present: string[] = [];
  if (consentWorkflowId) present.push(consentStatus ?? '');
  if (screenerWorkflowId) present.push(screenerStatus ?? '');

  if (present.every((s) => SUCCESS_TERMINAL_STATUSES.has(s))) {
    return 'success';
  }
  if (present.some((s) => s === 'CANCELLED')) {
    return 'cancelled';
  }
  if (present.some((s) => s === 'SYNC_FAILED')) {
    return 'sync_failed';
  }
  return 'sync_failed';
}

interface SessionApiResponse {
  success: boolean;
  message?: string;
  data: {
    sessionId: string;
    consentWorkflowId: string | null;
    screenerWorkflowId: string | null;
    consentStatus: string | null;
    screenerStatus: string | null;
    smsSent: boolean;
  } | null;
}

type Phase = 'loading' | 'consent' | 'screeners' | 'complete';

const PatientSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();

  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [consentWorkflowId, setConsentWorkflowId] = useState<string | null>(null);
  const [screenerWorkflowId, setScreenerWorkflowId] = useState<string | null>(null);
  const [completionOutcome, setCompletionOutcome] = useState<CompletionOutcome>('success');

  useEffect(() => {
    if (!sessionId || !UUID_REGEX.test(sessionId)) {
      setError('Invalid session link.');
      return;
    }

    let cancelled = false;

    axiosInstance
      .get<SessionApiResponse>(`/public/api/patient-session/${encodeURIComponent(sessionId)}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (!data) {
          setError('Unable to load session.');
          return;
        }

        setConsentWorkflowId(data.consentWorkflowId);
        setScreenerWorkflowId(data.screenerWorkflowId);

        const consentDone =
          !data.consentWorkflowId || TERMINAL_STATUSES.has(data.consentStatus ?? '');
        const screenersDone =
          !data.screenerWorkflowId || TERMINAL_STATUSES.has(data.screenerStatus ?? '');

        if (consentDone && screenersDone) {
          setCompletionOutcome(
            resolveCompletionOutcome(
              data.consentWorkflowId,
              data.consentStatus,
              data.screenerWorkflowId,
              data.screenerStatus,
            ),
          );
          setPhase('complete');
        } else if (!consentDone) {
          setPhase('consent');
        } else {
          setPhase('screeners');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load patient session:', err);
        setError(getApiErrorMessage(err, 'Unable to load session. Please try again.'));
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleConsentComplete = useCallback(() => {
    if (screenerWorkflowId) {
      setPhase('screeners');
    } else {
      setCompletionOutcome('success');
      setPhase('complete');
    }
  }, [screenerWorkflowId]);

  const handleScreenersComplete = useCallback(() => {
    setCompletionOutcome('success');
    setPhase('complete');
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      {/* Header */}
      <div className="flex flex-row px-4 py-4 sm:px-10 sm:py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-base sm:text-2xl">Patient Forms</span>
      </div>

      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        {error && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert type="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {phase === 'loading' && !error && (
          <div className="text-sm text-[#5D6A85] mb-2">Loading&hellip;</div>
        )}

        {phase === 'consent' && consentWorkflowId && (
          <ConsentPageContent
            workflowId={consentWorkflowId}
            onComplete={handleConsentComplete}
          />
        )}

        {phase === 'screeners' && screenerWorkflowId && (
          <PatientScreenersContent
            workflowId={screenerWorkflowId}
            onComplete={handleScreenersComplete}
          />
        )}

        {phase === 'complete' && (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-4 sm:p-10 w-full max-w-[1200px] mt-4">
            {completionOutcome === 'success' && (
              <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-[#01545D] mb-1">All Forms Completed</div>
                  <div className="text-sm text-[#1C1C1C]">
                    Thank you! All your forms have been completed successfully.
                  </div>
                </div>
              </div>
            )}
            {completionOutcome === 'cancelled' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-6 py-5 flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-blue-800 mb-1">Session cancelled</div>
                  <div className="text-sm text-[#1C1C1C]">
                    This form session is no longer active. If you still need to complete paperwork,
                    please use the link from your latest message or contact your care team.
                  </div>
                </div>
              </div>
            )}
            {completionOutcome === 'sync_failed' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-6 py-5 flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-orange-900 mb-1">
                    We couldn&apos;t finish processing your forms
                  </div>
                  <div className="text-sm text-[#1C1C1C]">
                    Your responses may not have been saved to your chart yet. Please try again in a
                    few minutes or contact your care team if this keeps happening.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default PatientSessionPage;
